import { FiBook } from 'react-icons/fi';

export const grammarRulesData = {
  courseId: 'grammar-rules',
  courseType: 'grammar',
  title: 'Advanced Grammar Rules',
  icon: FiBook,
  overview: {
    impact: 'Master fine points for Band 8+',
    focus: 'Articles, tenses, and common advanced errors',
    uses: 'All IELTS sections',
    duration: '30 minutes',
  },
  sections: [
    {
      title: 'Article Usage',
      content: `<p>Articles (a, an, the) are challenging but crucial for accuracy:</p>`,
      table: {
        headers: ['Rule', 'Example'],
        rows: [
          ['First mention = a/an', 'I saw a movie yesterday.'],
          ['Already mentioned = the', 'The movie was excellent.'],
          ['Unique things = the', 'the sun, the internet, the government'],
          ['General plurals = no article', 'Dogs are loyal animals.'],
          ['Uncountable (general) = no article', 'Water is essential.'],
        ],
      },
    },
    {
      title: 'Tense Consistency',
      content: `<p>Maintain consistent tenses within paragraphs unless signaling a time change:</p>`,
      examples: [
        { label: 'Inconsistent', text: 'The study shows that participants performed well and they improve over time.' },
        { label: 'Consistent', text: 'The study shows that participants performed well and improved over time.' },
      ],
      warning: 'Switching tenses without reason is a common error that affects your score.',
    },
    {
      title: 'Common Advanced Errors',
      content: `<p>Avoid these mistakes that even advanced learners make:</p>`,
      points: [
        'Double negative: "I don\'t have no money" → "I don\'t have any money"',
        'Wrong preposition: "interested for" → "interested in"',
        'Misused quantifiers: "less people" → "fewer people"',
        'Missing "be" in passive: "The work done yesterday" → "The work was done yesterday"',
        'Wrong word order in questions: "I wonder what is the answer" → "I wonder what the answer is"',
      ],
    },
  ],
  exercises: [
    {
      title: 'Choose the Correct Article',
      type: 'select',
      instructions: 'Select the correct article (or no article).',
      questions: [
        {
          text: '___ education is important for development.',
          options: ['The', 'An', '(no article)'],
          answer: '(no article)',
          explanation: 'General uncountable nouns don\'t need an article.',
        },
        {
          text: 'I\'m studying for ___ IELTS exam.',
          options: ['the', 'an', 'a'],
          answer: 'the',
          explanation: '"The" for a specific exam (IELTS).',
        },
        {
          text: '___ students in my class are from Asia.',
          options: ['The', 'A', '(no article)'],
          answer: 'The',
          explanation: '"The" for specific students (in my class).',
        },
        {
          text: 'She wants to become ___ engineer.',
          options: ['an', 'a', 'the'],
          answer: 'an',
          explanation: '"An" before vowel sounds; first mention of profession.',
        },
      ],
    },
  ],
  tips: [
    'Master article usage—it\'s crucial for accuracy',
    'Keep tenses consistent within paragraphs',
    'Learn common preposition combinations',
  ],
  dailyPractice: [
    'Review article rules with example sentences',
    'Check your writing for tense consistency',
    'Practice common error corrections',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Advanced Grammar Rules: Fine points including articles, tense consistency, and common advanced errors that affect IELTS scores.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...grammarRulesData,
      title: grammarRulesData.title + ' (Foundations)',
      overview: {
        ...grammarRulesData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: grammarRulesData.sections.slice(0, Math.max(2, Math.ceil(grammarRulesData.sections.length / 2))),
      exercises: grammarRulesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...grammarRulesData,
      title: grammarRulesData.title + ' (Advanced)',
      overview: {
        ...grammarRulesData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: grammarRulesData.sections,
      exercises: grammarRulesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(grammarRulesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return grammarRulesData;
}
